import { type OrganizationBase, type Pagination, type PaginationData, createOrganizationBase } from 'models';
import { useInfiniteQuery } from 'query';
import type { ApiClientResponseError, DefaultResponseErrorDataObject } from 'common/services';
import type { OrganizationScope } from '../models';
import { getPublicOrganizations } from '../services';
import { getPublicOrganizationsInfiniteQueryKey } from './keys';

type P = {
  filter?: PublicOrganizationsListFilter;
  options?: { enabled?: boolean };
  pagination: Omit<Pagination, 'position'>;
};

type PublicOrganizationsListFilter = {
  communityId?: string;
  groupId?: string;
  scope?: OrganizationScope;
  search?: string;
};

export const usePublicOrganizationsInfiniteQuery = ({ filter, options, pagination }: P) =>
  useInfiniteQuery<
    PaginationData<OrganizationBase>,
    string | undefined,
    ApiClientResponseError<DefaultResponseErrorDataObject>
  >({
    async queryFn({ pageParam: cursor }) {
      const { data: organizations } = await getPublicOrganizations({
        community_id: filter?.communityId,
        cursor,
        group_id: filter?.groupId,
        limit: pagination.pageSize,
        scope: filter?.scope,
        search: filter?.search,
      });

      return {
        data: organizations.data.map((organization) => createOrganizationBase(organization)),
        total: organizations.total,
      };
    },
    initialPageParam: undefined,
    getNextPageParam(lastPage) {
      if (lastPage.data.length < pagination.pageSize) {
        return undefined;
      }

      return lastPage.data.at(-1)?.id;
    },
    queryKey: getPublicOrganizationsInfiniteQueryKey({ filter, pagination }),
    throwOnError: true,
    ...options,
  });
